import { Supplier } from '../hooks/useSuppliers'
import { DetailedSupplier } from '../types/suppliers'

// Transform backend supplier to frontend DetailedSupplier format
export class SupplierService {
  static transformToDetailedSupplier(supplier: Supplier): DetailedSupplier {
    // Generate mock contact data for now (in real app, this would come from backend)
    const primaryContact = {
      id: `contact-${supplier.id}-1`,
      name: supplier.contactEmail
        .split('@')[0]
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      role: 'Primary Contact',
      phone: supplier.contactPhone,
      email: supplier.contactEmail,
      photo: `https://ui-avatars.com/api/?name=${encodeURIComponent(supplier.name)}&background=random`,
      availability: 'available' as const,
    }

    // Map backend status to frontend status
    const statusMap: Record<string, DetailedSupplier['status']> = {
      on_site: 'on_site',
      en_route: 'en_route',
      confirmed: 'confirmed',
      delayed: 'delayed',
      unconfirmed: 'confirmed', // Map unconfirmed to confirmed for frontend compatibility
    }

    return {
      id: supplier.id,
      name: supplier.name,
      role: supplier.role,
      status: statusMap[supplier.status] || 'confirmed',
      color: supplier.color || '#4A5568',
      contacts: [primaryContact],
      primaryContactId: primaryContact.id,
      currentTasks: [], // Will be populated from tasks API in phase 2
      compliance: {
        overallStatus: 'compliant', // Default for now
        insurance: {
          status: 'approved',
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        },
        certifications: {
          status: 'approved',
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        },
        riskAssessment: {
          status: 'approved',
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        },
      },
      rating: 4.5,
      totalEventsWithVenue: 10,
      teamSize: '3-5',
      equipment: [],
      specialInstructions: '',
      vehicleInfo: undefined,
      preferredPaymentMethod: 'bank_transfer',
      paymentTerms: 'net30',
      locationTracking: {
        enabled: false,
        lastUpdate: supplier.lastContact ? new Date(supplier.lastContact) : undefined,
      },
    }
  }

  static transformToBackendSupplier(supplier: DetailedSupplier): Partial<Supplier> {
    const primaryContact =
      supplier.contacts.find((c) => c.id === supplier.primaryContactId) || supplier.contacts[0]

    // Map frontend status to backend status
    const statusMap: Record<DetailedSupplier['status'], Supplier['status']> = {
      on_site: 'on_site',
      en_route: 'en_route',
      confirmed: 'confirmed',
      delayed: 'delayed',
    }

    return {
      id: supplier.id,
      name: supplier.name,
      role: supplier.role,
      contactEmail: primaryContact?.email || '',
      contactPhone: primaryContact?.phone || '',
      color: supplier.color,
      status: statusMap[supplier.status] || 'unconfirmed',
    }
  }

  static searchSuppliers(suppliers: DetailedSupplier[], searchTerm: string): DetailedSupplier[] {
    const term = searchTerm.toLowerCase()

    return suppliers.filter((supplier) => {
      // Search in supplier name and role
      if (
        supplier.name.toLowerCase().includes(term) ||
        supplier.role.toLowerCase().includes(term)
      ) {
        return true
      }

      // Search in contacts
      return supplier.contacts.some(
        (contact) =>
          contact.name.toLowerCase().includes(term) ||
          contact.email.toLowerCase().includes(term) ||
          contact.phone.includes(term) ||
          contact.role.toLowerCase().includes(term)
      )
    })
  }
}
